import axios from 'axios';

// Safe localStorage helpers — iOS Safari in Private mode and some in-app
// email browsers throw QuotaExceededError / SecurityError on access.
const safeLocalGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const safeLocalSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Ignore — storage may be unavailable (private mode, in-app browsers, etc.)
  }
};

export const handleTokenAndPostRequest = async (url, data, headers = {}, config = {}) => {
  try {
    // Demo-mode safety net. When a page is showing a sample/demo view
    // (window.__demoMode === true, set via useDemoNetworkGuard), no request
    // is allowed to reach the backend. We resolve with a benign, empty-shaped
    // response so optimistic UI updates stick locally but nothing persists.
    // This is belt-and-suspenders behind the per-page demo gating, so even an
    // un-gated handler can never mutate real data while a demo is on screen.
    if (typeof window !== 'undefined' && window.__demoMode) {
      console.warn('[demo] blocked backend request (demo mode):', url);
      // First give the demo resolver a chance to serve rich sample data for
      // this endpoint (threads, activity, notes, searches). Falls back to a
      // benign "success" shape so action handlers treat the call as a no-op
      // success (e.g. a draft Send removes the card locally) rather than
      // surfacing an error. Pages showing demos also short-circuit their
      // refetch while window.__demoMode is set, so seeded data is never lost.
      if (typeof window.__demoResolver === 'function') {
        try {
          const resolved = window.__demoResolver(url, data);
          if (resolved) return resolved;
        } catch (e) {
          console.warn('[demo] resolver error:', e);
        }
      }
      return {
        data: {
          status: 'success', success: true, demo: true,
          items: [], contacts: [], total_count: 0,
          balance: null, draft: null, message: '',
        },
      };
    }

    // Get 'jwt_token' from local storage
    let jwt_token = safeLocalGet('jwt_token');

    // Get 'token' from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const param_token = urlParams.get('token');

    // If 'token' exists in URL, persist it for later requests (best-effort)
    if (param_token) {
      safeLocalSet('param_token', param_token);
    }

    // Prefer the URL token directly so flows like /unsubscribe-agent work
    // even when localStorage is unavailable (iOS Safari private mode, etc.)
    const storedParamToken = param_token || safeLocalGet('param_token');

    // Check if data is FormData
    if (data instanceof FormData) {
      // Append additional fields to FormData
      data.append('param_token', storedParamToken);
      data.append('source', window.location.href);
    } else {
      // Handle non-FormData cases
      data = {
        ...data,
        param_token: storedParamToken,
        source: window.location.href,
      };
    }

    // Add 'Authorization' header
    const requestHeaders = {
      ...headers,
      'Authorization': `Bearer ${jwt_token}`,
    };

    // For FormData, let the browser set the correct Content-Type with boundary
    if (data instanceof FormData) {
      delete requestHeaders['Content-Type'];
    }

    // Make the POST request
    const response = await axios.post(url, data, { headers: requestHeaders, ...config });

    return response;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};